import * as THREE from 'three';
import type { EntityRegistry } from '../../entities';
import type { Asteroid, Landable } from '../sector';
import type { PlayerShip } from '../ships';
import type { NpcShip } from './NpcShip';
import type { MiningBeam } from './MiningBeam';
import type { HostileProjectiles } from './HostileProjectiles';
import type { Explosions } from '../combat';

export interface AsteroidClaims {
  /** Returns true if the claim succeeded; false if the asteroid is already taken. */
  claim(npc: NpcShip, asteroid: Asteroid): boolean;
  release(npc: NpcShip): void;
  isClaimed(asteroid: Asteroid): boolean;
  claimedBy(npc: NpcShip): Asteroid | null;
}

/** A patrol hub — anything that exposes a current world position. Landables
 *  satisfy this structurally; the station is wrapped to fit the same shape.
 *  Identity is by object reference, so beats can be released later. */
export interface PatrolHub {
  getWorldPosition(out: THREE.Vector3): THREE.Vector3;
}

/** A patrol assignment handed out by the director. `anchor` keeps a guard
 *  orbiting one hub; `lane` makes them shuttle between two hubs, which is
 *  the system's "highway patrol" — they intercept anything moving between
 *  planets/stations rather than just sitting on top of them. */
export type PatrolBeat =
  | { readonly kind: 'anchor'; readonly hub: PatrolHub }
  | {
      readonly kind: 'lane';
      readonly from: PatrolHub;
      readonly to: PatrolHub;
    };

export interface PatrolBeatProvider {
  /** Pick the most undermanned beat. Always returns one — there's always a
   *  hub to assign even if every quota is full (overflow goes onto the
   *  least-anchored hub). */
  acquire(): PatrolBeat;
  release(beat: PatrolBeat): void;
}

export interface NpcBrainContext {
  readonly ship: NpcShip;
  readonly stationPos: THREE.Vector3;
  readonly landables: readonly Landable[];
  readonly entities: EntityRegistry;
  readonly getPlayerShip: () => PlayerShip | null;
  readonly beam: MiningBeam | null;
  readonly hostileProjectiles: HostileProjectiles;
  readonly claims: AsteroidClaims;
  readonly explosions: Explosions;
  readonly patrolBeats: PatrolBeatProvider;
}

export interface NpcBrain {
  readonly finished: boolean;
  isHostile(): boolean;
  update(dt: number): void;
  onAttackedByPlayer(): void;
  clearHostility(): void;
  dispose(): void;
}

const _matrix = new THREE.Matrix4();
const _quat = new THREE.Quaternion();
const _up = new THREE.Vector3(0, 1, 0);
const _tmpLook = new THREE.Vector3();
const _tmpDir = new THREE.Vector3();

/** Rotate ship so its local -Z axis points at `target`. */
export function faceTarget(
  ship: NpcShip,
  target: THREE.Vector3,
  dt: number,
  turnRate: number
): void {
  _matrix.lookAt(ship.object3D.position, target, _up);
  _quat.setFromRotationMatrix(_matrix);
  const t = 1 - Math.exp(-turnRate * dt);
  ship.object3D.quaternion.slerp(_quat, t);
}

/** Blend velocity toward `direction * maxSpeed`. Direction should be normalized. */
export function applyThrust(
  ship: NpcShip,
  direction: THREE.Vector3,
  dt: number,
  maxSpeed: number,
  accel: number
): void {
  _tmpDir.copy(direction).multiplyScalar(maxSpeed);
  const blend = 1 - Math.exp(-accel * dt);
  ship.velocity.lerp(_tmpDir, blend);
  const speed = ship.velocity.length();
  if (speed > maxSpeed) ship.velocity.multiplyScalar(maxSpeed / speed);
  ship.setThrust(Math.min(speed / maxSpeed + 0.15, 1));
}

/**
 * Navigate toward a target: rotate to face it AND thrust forward.
 * Used for long-distance travel (patrol, returning, approach).
 */
export function steerToward(
  ship: NpcShip,
  target: THREE.Vector3,
  dt: number,
  cruiseSpeed: number,
  accel: number,
  turnRate: number
): number {
  _tmpLook.copy(target).sub(ship.object3D.position);
  const distance = _tmpLook.length();
  if (distance < 1e-4) {
    ship.velocity.multiplyScalar(Math.pow(0.7, dt * 60));
    ship.setThrust(0);
    return 0;
  }
  _tmpLook.multiplyScalar(1 / distance);

  faceTarget(ship, target, dt, turnRate);
  applyThrust(ship, _tmpLook, dt, cruiseSpeed, accel);
  return distance;
}

export function holdPosition(
  ship: NpcShip,
  lookAt: THREE.Vector3,
  dt: number,
  turnRate: number
): void {
  ship.velocity.multiplyScalar(Math.pow(0.85, dt * 60));
  ship.setThrust(0);
  faceTarget(ship, lookAt, dt, turnRate);
}


export function pickRandomPoint(
  center: THREE.Vector3,
  radius: number,
  verticalSpread: number,
  out: THREE.Vector3
): THREE.Vector3 {
  const angle = Math.random() * Math.PI * 2;
  const r = radius * (0.6 + Math.random() * 0.4);
  out.set(
    center.x + Math.cos(angle) * r,
    center.y + (Math.random() - 0.5) * verticalSpread,
    center.z + Math.sin(angle) * r
  );
  return out;
}

export function createAsteroidClaims(): AsteroidClaims {
  const claimsByNpc = new Map<number, Asteroid>();
  // Track owner per asteroid so we can reject double claims and release safely.
  const ownerByAsteroid = new Map<Asteroid, number>();

  return {
    claim(npc, asteroid) {
      const npcKey = npc.id as unknown as number;
      const existingOwner = ownerByAsteroid.get(asteroid);
      if (existingOwner !== undefined && existingOwner !== npcKey) {
        return false;
      }
      const prev = claimsByNpc.get(npcKey);
      if (prev && prev !== asteroid) {
        if (ownerByAsteroid.get(prev) === npcKey) {
          ownerByAsteroid.delete(prev);
        }
      }
      claimsByNpc.set(npcKey, asteroid);
      ownerByAsteroid.set(asteroid, npcKey);
      return true;
    },
    release(npc) {
      const npcKey = npc.id as unknown as number;
      const prev = claimsByNpc.get(npcKey);
      if (prev) {
        if (ownerByAsteroid.get(prev) === npcKey) {
          ownerByAsteroid.delete(prev);
        }
        claimsByNpc.delete(npcKey);
      }
    },
    isClaimed(asteroid) {
      return ownerByAsteroid.has(asteroid);
    },
    claimedBy(npc) {
      return claimsByNpc.get(npc.id as unknown as number) ?? null;
    }
  };
}
